import { useEffect, useState } from "react";
import { Phone, MessageCircle, CalendarDays, Clock, Gem, Mail } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Reveal } from "@/components/Reveal";

const fmt = (iso) =>
  new Date(iso).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });

const waDigits = (phone) => (phone || "").replace(/\D/g, "");

const TABS = [
  { key: "consultations", label: "Consultations" },
  { key: "contacts", label: "Enquiries" },
  { key: "newsletter", label: "Circle" },
];

export default function Requests() {
  const [key, setKey] = useState(localStorage.getItem("merakee_analytics_key") || "");
  const [input, setInput] = useState("");
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("consultations");
  const [products, setProducts] = useState({});
  const [loading, setLoading] = useState(false);

  const load = async (k) => {
    setLoading(true);
    try {
      const r = await api.get("/requests/summary", { params: { key: k } });
      setData(r.data);
      setKey(k);
      localStorage.setItem("merakee_analytics_key", k);
    } catch {
      toast.error("Incorrect passcode.");
      localStorage.removeItem("merakee_analytics_key");
      setKey("");
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    api.get("/products").then((r) => {
      const map = {};
      r.data.forEach((p) => (map[p.slug] = p.name));
      setProducts(map);
    }).catch(() => {});
    if (key) load(key);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!data) {
    return (
      <div className="flex min-h-screen items-center justify-center px-6 pt-20" data-testid="requests-gate">
        <div className="w-full max-w-sm border border-[#A58343]/25 bg-[#FFFFFF] p-10 text-center">
          <p className="overline">Private</p>
          <h1 className="mt-4 font-serif text-4xl font-light text-[#262019]">Booking Requests</h1>
          <p className="mt-3 text-sm text-[#7C7163]">Same passcode as your analytics page.</p>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              load(input);
            }}
            className="mt-8"
          >
            <input
              type="password"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Passcode"
              data-testid="requests-passcode-input"
              className="field text-center"
            />
            <button type="submit" disabled={loading} className="btn-gold-solid mt-8 w-full" data-testid="requests-open-button">
              {loading ? "Opening…" : "Open Requests"}
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-4xl px-6 pb-24 pt-40 lg:px-8" data-testid="requests-page">
      <Reveal>
        <p className="overline">Private · Only you can see this</p>
        <div className="mt-4 flex flex-wrap items-end justify-between gap-6">
          <h1 className="font-serif text-4xl font-light text-[#262019] sm:text-5xl">
            Booking <span className="italic text-[#A58343]">Requests</span>
          </h1>
          <button onClick={() => load(key)} className="btn-gold !px-6 !py-2.5" data-testid="requests-refresh-button">
            Refresh
          </button>
        </div>
      </Reveal>

      <div className="mt-10 flex flex-wrap gap-3" data-testid="requests-tabs">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            data-testid={`tab-${t.key}`}
            className={`rounded-full border px-6 py-2.5 text-xs uppercase tracking-[0.2em] transition-colors duration-300 ${
              tab === t.key
                ? "border-[#A58343] bg-[#A58343] text-[#FAF7F1]"
                : "border-[#A58343]/40 text-[#8C6D33] hover:border-[#A58343]"
            }`}
          >
            {t.label} · {data[t.key].length}
          </button>
        ))}
      </div>

      {tab === "consultations" && (
        <div className="mt-10 space-y-6" data-testid="consultations-list">
          {data.consultations.length === 0 && (
            <p className="text-sm text-[#7C7163]">No consultation requests yet — they will appear here the moment someone books.</p>
          )}
          {data.consultations.map((c) => (
            <div key={c.id} className="border border-[#A58343]/20 bg-[#FFFFFF] p-8" data-testid={`consultation-${c.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <h3 className="font-serif text-2xl font-medium text-[#262019]">{c.name}</h3>
                  <p className="mt-1 text-xs tracking-[0.15em] text-[#7C7163]">{fmt(c.created_at)}</p>
                </div>
                <span className="rounded-full border border-[#A58343]/40 px-4 py-1.5 text-[10px] uppercase tracking-[0.2em] text-[#8C6D33]">
                  {c.status || "new"}
                </span>
              </div>
              <div className="mt-6 grid gap-4 border-t border-[#A58343]/10 pt-6 text-sm sm:grid-cols-2">
                <div className="flex items-center gap-3">
                  <Phone size={15} strokeWidth={1.5} className="shrink-0 text-[#A58343]" />
                  <a href={`tel:${c.phone}`} className="text-[#262019] hover:text-[#A58343]">{c.phone}</a>
                  <a
                    href={`https://wa.me/${waDigits(c.phone)}`}
                    target="_blank"
                    rel="noreferrer"
                    data-testid={`wa-${c.id}`}
                    className="text-[#8C6D33] hover:text-[#A58343]"
                    aria-label="WhatsApp"
                  >
                    <MessageCircle size={15} strokeWidth={1.5} />
                  </a>
                </div>
                <div className="flex items-center gap-3">
                  <Mail size={15} strokeWidth={1.5} className="shrink-0 text-[#A58343]" />
                  <span className="truncate text-[#262019]">{c.email}</span>
                </div>
                <div className="flex items-center gap-3">
                  <CalendarDays size={15} strokeWidth={1.5} className="shrink-0 text-[#A58343]" />
                  <span className="text-[#262019]">{c.preferred_date}</span>
                </div>
                <div className="flex items-center gap-3">
                  <Clock size={15} strokeWidth={1.5} className="shrink-0 text-[#A58343]" />
                  <span className="text-[#262019]">{c.preferred_time}</span>
                </div>
              </div>
              {c.product_interest && (
                <div className="mt-4 flex items-center gap-3 text-sm">
                  <Gem size={15} strokeWidth={1.5} className="shrink-0 text-[#A58343]" />
                  <span className="text-[#262019]">
                    Interested in: {products[c.product_interest] || c.product_interest}
                  </span>
                </div>
              )}
              {c.message && (
                <p className="mt-4 border-l-2 border-[#A58343]/30 pl-4 text-sm italic leading-relaxed text-[#7C7163]">
                  “{c.message}”
                </p>
              )}
            </div>
          ))}
        </div>
      )}

      {tab === "contacts" && (
        <div className="mt-10 space-y-6" data-testid="contacts-list">
          {data.contacts.length === 0 && (
            <p className="text-sm text-[#7C7163]">No enquiries yet.</p>
          )}
          {data.contacts.map((c) => (
            <div key={c.id} className="border border-[#A58343]/20 bg-[#FFFFFF] p-8" data-testid={`contact-${c.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-4">
                <h3 className="font-serif text-2xl font-medium text-[#262019]">{c.name}</h3>
                <p className="text-xs tracking-[0.15em] text-[#7C7163]">{fmt(c.created_at)}</p>
              </div>
              <p className="mt-2 text-sm text-[#7C7163]">
                {c.email}{c.phone ? ` · ${c.phone}` : ""}
              </p>
              <p className="mt-4 border-l-2 border-[#A58343]/30 pl-4 text-sm italic leading-relaxed text-[#7C7163]">
                “{c.message}”
              </p>
            </div>
          ))}
        </div>
      )}

      {tab === "newsletter" && (
        <div className="mt-10" data-testid="newsletter-list">
          {data.newsletter.length === 0 && (
            <p className="text-sm text-[#7C7163]">No one has joined the circle yet.</p>
          )}
          <div className="border border-[#A58343]/20 bg-[#FFFFFF]">
            {data.newsletter.map((n, i) => (
              <div
                key={n.id || n.email}
                className={`flex items-center justify-between gap-4 px-8 py-5 text-sm ${i > 0 ? "border-t border-[#A58343]/10" : ""}`}
              >
                <span className="truncate text-[#262019]">{n.email}</span>
                <span className="shrink-0 text-xs text-[#7C7163]">{fmt(n.created_at)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
